import { useEffect, useRef } from "react";
import { MathUtils, Vector3 } from "three";
import { useFrame } from "@react-three/fiber";
import { useControls } from "leva";
import { useKeyboardControls } from "@react-three/drei";
import { degToRad } from "three/src/math/MathUtils.js";
import useGame from "./stores/useGame";

import runSound from '../public/audio/run.mp3';
import flySound from '../public/audio/fly.mp3';

const normalizaAngle = (angle) => {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;
    return angle;
};

const lerpAngle = (start, end, t) => {
    start = normalizaAngle(start);
    end = normalizaAngle(end);

    if (Math.abs(end - start) > Math.PI) {
        if (end > start) {
            start += 2 * Math.PI;
        } else {
            end += 2 * Math.PI;
        }
    }

    return MathUtils.lerp(start, end, t);
};

export default function MovementController({ rb, character, setCurrentAction, actions, handleMove, handleJump, handleRun }) {
    const start = useGame((state) => state.start);
    const restart = useGame((state) => state.restart);

    const { WALK_SPEED, RUN_SPEED, ROTATION_SPEED } = useControls("Character Control", {
        WALK_SPEED: { value: 2.5, min: 0.1, max: 4, step: 0.1 },
        RUN_SPEED: { value: 4.5, min: 0.2, max: 12, step: 0.1 },
        ROTATION_SPEED: {
            value: degToRad(120),
            min: degToRad(10),
            max: degToRad(180),
            step: degToRad(10),
        },
    });

    const characterRotationTarget = useRef(0);
    const [, get] = useKeyboardControls();
    const isJumping = useRef(false);
    const isRunning = useRef(false);
    const isSpelling = useRef(false);

    const runAudioRef = useRef(new Audio(runSound));
    const flyAudioRef = useRef(new Audio(flySound));

    const handleKeyChange = (event) => {
        switch (event.code) {
            case 'Space':
                isJumping.current = event.type === 'keydown';
                setCurrentAction(isJumping.current ? 'jumping' : 'iddle');
                if (event.type === 'keydown' && flyAudioRef.current.paused) {
                    flyAudioRef.current.play();
                }
                break;
            case 'ShiftLeft':
            case 'ShiftRight':
                isRunning.current = event.type === 'keydown';
                setCurrentAction(isRunning.current ? 'running' : 'iddle');
                if (event.type === 'keydown' && runAudioRef.current.paused) {
                    runAudioRef.current.play();
                }
                if (event.type === 'keyup' && !runAudioRef.current.paused) {
                    runAudioRef.current.pause();
                }
                break;
            case 'ControlLeft':
            case 'ControlRight':
                isSpelling.current = event.type === 'keydown';
                setCurrentAction(isSpelling.current ? 'spelling' : 'iddle');
                break;
            default:
                break;
        }
    };

    const handleTouchMove = (event) => {
        // Maneja el movimiento basado en el evento táctil
        handleMove(event);
    };

    const handleTouchStart = (event) => {
        // Maneja los eventos táctiles para saltar y correr
        handleJump(event);
        handleRun(event);
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyChange);
        window.addEventListener('keyup', handleKeyChange);

        return () => {
            window.removeEventListener('keydown', handleKeyChange);
            window.removeEventListener('keyup', handleKeyChange);
        };
    }, []);

    useFrame((state, delta) => {
        if (rb.current) {
            const vel = rb.current.linvel();
            const movement = { x: 0, z: 0 };

            if (get().forward) {
                movement.z = -1;
                start();
                if (setCurrentAction !== 'walking' && !isRunning.current && !isSpelling.current) {
                    setCurrentAction('walking');
                }
            } else if (get().backward) {
                movement.z = 1;
                start();
                if (setCurrentAction !== 'walkingBack' && !isRunning.current && !isSpelling.current) {
                    setCurrentAction('walkingBack');
                }
            } else if (get().left) {
                characterRotationTarget.current += ROTATION_SPEED * 0.01;
                if (setCurrentAction !== 'turningL' && !isRunning.current && !isSpelling.current) {
                    setCurrentAction('turningL');
                }
            } else if (get().right) {
                characterRotationTarget.current -= ROTATION_SPEED * 0.01;
                if (setCurrentAction !== 'turningR' && !isRunning.current && !isSpelling.current) {
                    setCurrentAction('turningR');
                }
            } else if (!isJumping.current && !isRunning.current && !isSpelling.current) {
                setCurrentAction('iddle'); // Vuelve a la animación "iddle" si no hay movimiento
                
                if (!runAudioRef.current.paused) {
                    runAudioRef.current.pause();
                }
            }

            let speed = isRunning.current ? RUN_SPEED : WALK_SPEED;

            if (movement.z !== 0) {
                vel.x = Math.sin(characterRotationTarget.current) * speed * movement.z;
                vel.z = Math.cos(characterRotationTarget.current) * speed * movement.z;
            }

            if (isJumping.current && rb.current.translation().y < 1) {
                vel.y = 5;
                isJumping.current = false;
            }

            character.current.rotation.y = lerpAngle(
                character.current.rotation.y,
                characterRotationTarget.current,
                0.1
            );

            rb.current.setLinvel(vel, true);

            // Verificación de reinicio
            if (rb.current.translation().y < -8) {
                restart();
            }
        }
    });

    return null;
}
